import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * PostgreSQL Node Schema
 * Supports execute query, insert, update, delete, and table management operations
 */
const schema: NodePropertySchema = {
  nodeType: 'postgresql',
  version: '2.0.0',

  properties: [
    // ==================================================================
    // CREDENTIAL SELECTOR
    // ==================================================================
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select the PostgreSQL credential to use',
      credentialTypes: [{ name: 'postgresql', required: true }],
    },

    // ==================================================================
    // OPERATION
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'executeQuery',
      options: [
        { value: 'executeQuery', name: 'Execute Query', description: 'Execute a SQL query' },
        { value: 'insert', name: 'Insert Rows', description: 'Insert rows from input array into a table' },
        { value: 'update', name: 'Update Rows', description: 'Update rows in a table using input array' },
        { value: 'delete', name: 'Delete Rows', description: 'Delete rows matching a condition' },
        { value: 'createTable', name: 'Create Table', description: 'Create a table from array data structure' },
        { value: 'bulkInsert', name: 'Bulk Insert', description: 'Insert all rows from an array' },
        { value: 'dropTable', name: 'Drop Table', description: 'Drop (delete) a table' },
        { value: 'truncateTable', name: 'Truncate Table', description: 'Remove all rows from a table' },
        { value: 'tableExists', name: 'Table Exists', description: 'Check if a table exists' },
        { value: 'getTableSchema', name: 'Get Table Schema', description: 'Get column definitions for a table' },
      ],
    },

    // ==================================================================
    // EXECUTE QUERY
    // ==================================================================
    {
      key: 'query',
      label: 'Query',
      type: 'string',
      required: true,
      typeOptions: {
        rows: 10,
        editor: 'code',
        editorLanguage: 'sql',
      },
      placeholder: 'SELECT * FROM users WHERE status = $1',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
      description: 'The SQL query to execute. Use $1, $2, etc. for parameters.',
    },

    // ==================================================================
    // QUERY PARAMETERS (executeQuery only)
    // ==================================================================
    {
      key: 'parameters',
      label: 'Query Parameters',
      type: 'collection',
      displayOptions: {
        show: {
          operation: ['executeQuery']
        }
      },
      default: {},
      placeholder: 'Add Parameter',
      description: 'Query parameters for parameterized queries',
      values: [
        {
          key: 'name',
          label: 'Parameter Name',
          type: 'string',
          default: '',
          placeholder: 'status',
        },
        {
          key: 'value',
          label: 'Value',
          type: 'string',
          default: '',
          placeholder: 'active',
        }
      ]
    },

    // ==================================================================
    // TABLE NAME (for table operations)
    // ==================================================================
    {
      key: 'table',
      label: 'Table',
      type: 'string',
      required: true,
      placeholder: 'users',
      displayOptions: {
        show: {
          operation: ['insert', 'update', 'delete', 'createTable', 'bulkInsert', 'dropTable', 'truncateTable', 'tableExists', 'getTableSchema'],
        },
      },
      description: 'Name of the table to operate on',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getTables',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // SOURCE ARRAY (for insert, update, createTable, bulkInsert)
    // ==================================================================
    {
      key: 'sourceArray',
      label: 'Source Data',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'Drag an array from a previous node. Each object in the array will be inserted/updated as a row.',
      displayOptions: {
        show: {
          operation: ['insert', 'update', 'createTable', 'bulkInsert'],
        },
      },
    },

    // ==================================================================
    // UPDATE - MATCH COLUMN
    // ==================================================================
    {
      key: 'matchColumn',
      label: 'Match Column',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['update']
        }
      },
      default: 'id',
      placeholder: 'id',
      required: true,
      description: 'Column to match rows for updating (typically the primary key). The source data must include this column.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getColumns',
          dependsOn: ['table'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // DELETE - CONDITION FIELDS
    // ==================================================================
    {
      key: 'deleteColumn',
      label: 'Column',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['delete']
        }
      },
      default: '',
      placeholder: 'id',
      required: true,
      description: 'Column to use for matching rows to delete',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getColumns',
          dependsOn: ['table'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'deleteCondition',
      label: 'Condition',
      type: 'options',
      displayOptions: {
        show: {
          operation: ['delete']
        }
      },
      default: 'equals',
      required: true,
      options: [
        { name: 'Equals', value: 'equals', description: 'Column value equals the specified value' },
        { name: 'Not Equals', value: 'notEquals', description: 'Column value does not equal the specified value' },
        { name: 'Greater Than', value: 'greaterThan', description: 'Column value is greater than the specified value' },
        { name: 'Less Than', value: 'lessThan', description: 'Column value is less than the specified value' },
        { name: 'Greater Or Equal', value: 'greaterOrEqual', description: 'Column value is greater than or equal to the specified value' },
        { name: 'Less Or Equal', value: 'lessOrEqual', description: 'Column value is less than or equal to the specified value' },
        { name: 'Like', value: 'like', description: 'Column value matches the pattern (use % for wildcards)' },
        { name: 'In List', value: 'in', description: 'Column value is in the comma-separated list of values' },
        { name: 'Is Null', value: 'isNull', description: 'Column value is NULL' },
        { name: 'Is Not Null', value: 'isNotNull', description: 'Column value is not NULL' },
      ],
      description: 'How to match rows for deletion',
    },
    {
      key: 'deleteValue',
      label: 'Value',
      type: 'string',
      displayOptions: {
        show: {
          operation: ['delete'],
          deleteCondition: ['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual', 'like', 'in']
        }
      },
      default: '',
      placeholder: '123',
      required: true,
      description: 'Value to compare against. For "In List", use comma-separated values.',
    },

    // ==================================================================
    // DROP TABLE OPTIONS
    // ==================================================================
    {
      key: 'ifExists',
      label: 'Only If Exists',
      type: 'boolean',
      default: true,
      description: 'Use IF EXISTS to prevent errors if the table does not exist',
      displayOptions: {
        show: {
          operation: ['dropTable'],
        },
      },
    },

    // ==================================================================
    // CREATE TABLE OPTIONS
    // ==================================================================
    {
      key: 'dropIfExists',
      label: 'Drop If Exists',
      type: 'boolean',
      default: false,
      description: 'Drop the table first if it already exists',
      displayOptions: {
        show: {
          operation: ['createTable'],
        },
      },
    },
    {
      key: 'insertData',
      label: 'Insert Data After Create',
      type: 'boolean',
      default: true,
      description: 'Insert the array data into the table after creating it',
      displayOptions: {
        show: {
          operation: ['createTable'],
        },
      },
    },

    // ==================================================================
    // SETTINGS
    // ==================================================================
    {
      key: 'queryTimeout',
      label: 'Query Timeout (seconds)',
      type: 'number',
      section: 'settings',
      default: 120,
      placeholder: '120',
      description: 'Maximum time to wait for query execution.',
      typeOptions: {
        minValue: 1
      },
    },
  ],
};

export default schema;
